"use client"

import { useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'
import Link from 'next/link'
import Image from 'next/image'
import { LocalShareButtons } from '@/components/blocks/local-share-buttons'
import { ChevronRight } from 'lucide-react'
import { STATE_SLUGS } from '@/lib/state-utils'
import { stateFlagStyle } from '@/lib/state-flag-sizes'

// Adjacent states (by region)
const ADJACENT_STATES: Record<string, string[]> = {
  // Northeast
  'ME': ['NH', 'VT', 'MA'], 'NH': ['ME', 'VT', 'MA'], 'VT': ['NH', 'NY', 'MA'],
  'MA': ['NH', 'CT', 'RI', 'NY'], 'RI': ['MA', 'CT'], 'CT': ['MA', 'NY', 'RI'],
  'NY': ['NJ', 'PA', 'CT', 'VT', 'MA'], 'NJ': ['NY', 'PA', 'DE'],
  'PA': ['NY', 'NJ', 'DE', 'MD', 'OH', 'WV'],
  // Southeast
  'DE': ['MD', 'PA', 'NJ'], 'MD': ['PA', 'DE', 'VA', 'WV', 'DC'],
  'VA': ['MD', 'WV', 'NC', 'TN', 'KY'], 'WV': ['PA', 'MD', 'VA', 'OH', 'KY'],
  'NC': ['VA', 'SC', 'GA', 'TN'], 'SC': ['NC', 'GA'],
  'GA': ['SC', 'FL', 'AL', 'TN', 'NC'], 'FL': ['GA', 'AL'],
  // South Central
  'AL': ['GA', 'FL', 'MS', 'TN'], 'MS': ['AL', 'TN', 'AR', 'LA'],
  'TN': ['KY', 'VA', 'NC', 'GA', 'AL', 'MS', 'AR', 'MO'],
  'KY': ['OH', 'WV', 'VA', 'TN', 'MO', 'IL', 'IN'],
  'LA': ['MS', 'AR', 'TX'], 'AR': ['MO', 'TN', 'MS', 'LA', 'TX', 'OK'],
  // Southwest
  'TX': ['LA', 'AR', 'OK', 'NM'], 'OK': ['KS', 'MO', 'AR', 'TX', 'NM', 'CO'],
  'NM': ['TX', 'OK', 'CO', 'AZ', 'UT'], 'AZ': ['NM', 'UT', 'NV', 'CA'],
  // West
  'CA': ['OR', 'NV', 'AZ'], 'NV': ['CA', 'OR', 'ID', 'UT', 'AZ'],
  'OR': ['WA', 'CA', 'NV', 'ID'], 'WA': ['OR', 'ID'],
  'ID': ['WA', 'OR', 'NV', 'UT', 'WY', 'MT'],
  'MT': ['ID', 'WY', 'ND', 'SD'], 'WY': ['MT', 'ID', 'UT', 'CO', 'NE', 'SD'],
  'UT': ['ID', 'WY', 'CO', 'NM', 'AZ', 'NV'], 'CO': ['WY', 'NE', 'KS', 'OK', 'NM', 'UT'],
  // Midwest
  'ND': ['MT', 'SD', 'MN'], 'SD': ['ND', 'MT', 'WY', 'NE', 'IA', 'MN'],
  'NE': ['SD', 'WY', 'CO', 'KS', 'MO', 'IA'], 'KS': ['NE', 'CO', 'OK', 'MO'],
  'MN': ['ND', 'SD', 'IA', 'WI'], 'IA': ['MN', 'SD', 'NE', 'MO', 'IL', 'WI'],
  'MO': ['IA', 'NE', 'KS', 'OK', 'AR', 'TN', 'KY', 'IL'],
  'WI': ['MN', 'IA', 'IL', 'MI'], 'IL': ['WI', 'IA', 'MO', 'KY', 'IN', 'MI'],
  'MI': ['WI', 'IL', 'IN', 'OH'], 'IN': ['MI', 'IL', 'KY', 'OH'],
  'OH': ['MI', 'IN', 'KY', 'WV', 'PA'],
  // Non-contiguous
  'AK': [], 'HI': [],
}

const STATE_NAMES: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
}

interface RebatePageClientProps {
  stateName: string
  stateCode: string
  rebateCount: number
  maxRebateAmount: number
  utilityCount: number
}

export function RebatePageClient({
  stateName,
  stateCode,
  rebateCount,
  maxRebateAmount,
  utilityCount
}: RebatePageClientProps) {
  const posthog = usePostHog()

  // Track page view
  useEffect(() => {
    if (posthog) {
      posthog.capture('rebate_state_viewed', {
        state_code: stateCode,
        state_name: stateName,
        rebate_count: rebateCount,
        max_rebate_amount: maxRebateAmount,
        utility_count: utilityCount
      })
    }
  }, [posthog, stateCode, stateName, rebateCount, maxRebateAmount, utilityCount])

  const adjacentStateCodes = ADJACENT_STATES[stateCode] || []
  const adjacentStates = adjacentStateCodes
    .filter(code => STATE_NAMES[code])
    .slice(0, 5)
    .map(code => ({
      code,
      name: STATE_NAMES[code],
      slug: STATE_SLUGS[code]
    }))

  return (
    <>
      {/* Share Buttons - Top */}
      <div className="mb-6">
        <LocalShareButtons
          locationName={`${stateName} Rebates`}
          variant="top"
          electricityRate={0} // Not relevant for rebate pages
          pageType="rebate"
        />
      </div>

      {/* Adjacent States Navigation */}
      {adjacentStates.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm text-gray-600 mb-2">
            Nearby State Rebates:
          </p>
          <div className="flex flex-wrap gap-2">
            {adjacentStates.map((st) => (
              <Link
                key={st.code}
                href={`/rebates/${st.slug}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-green-500 hover:text-green-600 transition-colors"
              >
                <Image
                  src={`/state-flags/fridge.com-${(st.code || '').toLowerCase()}-flag.png`}
                  style={stateFlagStyle(st.code)}
                  alt={`Flag of ${st.name}`}
                  width={28}
                  height={18}
                  className="rounded-sm border border-gray-200"
                />
                {st.name}
              </Link>
            ))}
            <Link
              href="/rebates"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-sm text-green-700 hover:bg-green-100 transition-colors"
            >
              All States
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </>
  )
}

// Bottom share section - to be placed at end of page content
export function RebatePageShareBottom({
  stateName,
  maxRebateAmount
}: {
  stateName: string
  maxRebateAmount: number
}) {
  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <LocalShareButtons
        locationName={`${stateName} Rebates`}
        variant="bottom"
        electricityRate={maxRebateAmount / 100} // Use rebate amount for share text
        pageType="rebate"
      />
    </div>
  )
}
